/**
 * Converts an RGB color value to HSL.
 * Conversion formula adapted from http://en.wikipedia.org/wiki/HSL_color_space.
 * Assumes r, g, and b are contained in the set [0, 255] and
 * returns h, s, and l in the set [0, 1].
 */
export function rgbToHsl(r, g, b) {
    r /= 255;
    g /= 255;
    b /= 255;

    const max = Math.max(r, g, b), min = Math.min(r, g, b);
    let h, s, l = (max + min) / 2;

    if (max === min) {
        h = s = 0; // achromatic
    } else {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        switch (max) {
            case r: h = (g - b) / d + (g < b ? 6 : 0); break;
            case g: h = (b - r) / d + 2; break;
            case b: h = (r - g) / d + 4; break;
        }
        h /= 6;
    }

    return [h, s, l];
}

export function hslToRgb(h, s, l) {
    let r, g, b;

    if (s === 0) {
        r = g = b = l; // achromatic
    } else {
        const hue2rgb = (p, q, t) => {
            if (t < 0) t += 1;
            if (t > 1) t -= 1;
            if (t < 1 / 6) return p + (q - p) * 6 * t;
            if (t < 1 / 2) return q;
            if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
            return p;
        };

        const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
        const p = 2 * l - q;
        r = hue2rgb(p, q, h + 1 / 3);
        g = hue2rgb(p, q, h);
        b = hue2rgb(p, q, h - 1 / 3);
    }

    return [Math.round(r * 255), Math.round(g * 255), Math.round(b * 255)];
}

export function rgbToHex(r, g, b) {
    return "#" + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
}

// Convert RGB to LAB (for better color distance/visualization if needed)
// Simplistic approximation or standard formula
export function rgbToLab(r, g, b) {
    // RGB to XYZ
    let rLinear = r / 255, gLinear = g / 255, bLinear = b / 255;
    rLinear = (rLinear > 0.04045) ? Math.pow((rLinear + 0.055) / 1.055, 2.4) : rLinear / 12.92;
    gLinear = (gLinear > 0.04045) ? Math.pow((gLinear + 0.055) / 1.055, 2.4) : gLinear / 12.92;
    bLinear = (bLinear > 0.04045) ? Math.pow((bLinear + 0.055) / 1.055, 2.4) : bLinear / 12.92;

    let x = (rLinear * 0.4124 + gLinear * 0.3576 + bLinear * 0.1805) * 100;
    let y = (rLinear * 0.2126 + gLinear * 0.7152 + bLinear * 0.0722) * 100;
    let z = (rLinear * 0.0193 + gLinear * 0.1192 + bLinear * 0.9505) * 100;

    // XYZ to LAB
    x /= 95.047;
    y /= 100.000;
    z /= 108.883;

    x = (x > 0.008856) ? Math.pow(x, 1 / 3) : (7.787 * x) + (16 / 116);
    y = (y > 0.008856) ? Math.pow(y, 1 / 3) : (7.787 * y) + (16 / 116);
    z = (z > 0.008856) ? Math.pow(z, 1 / 3) : (7.787 * z) + (16 / 116);

    return [
        (116 * y) - 16,
        500 * (x - y),
        200 * (y - z)
    ];
}


export const analyzeImage = (imageElement, sampleCount = 1000) => {
    return new Promise((resolve, reject) => {
        try {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            canvas.width = imageElement.naturalWidth;
            canvas.height = imageElement.naturalHeight;
            ctx.drawImage(imageElement, 0, 0);

            const { width, height } = canvas;
            const imageData = ctx.getImageData(0, 0, width, height).data;
            const samples = [];

            // Grid sampling or Random sampling? Let's do random for "Dynamic Dots" feel initially, 
            // but we want to cover the image. The user request has dots OVER the image. 
            // We can return the implementation specific positions as well.

            for (let i = 0; i < sampleCount; i++) {
                const x = Math.floor(Math.random() * width);
                const y = Math.floor(Math.random() * height);
                const index = (y * width + x) * 4;
                const r = imageData[index];
                const g = imageData[index + 1];
                const b = imageData[index + 2];
                // basic alpha check
                if (imageData[index + 3] < 128) continue;

                const hex = rgbToHex(r, g, b);
                const [h, s, l] = rgbToHsl(r, g, b);
                const lab = rgbToLab(r, g, b);

                samples.push({
                    x: x / width, // normalized position
                    y: y / height,
                    color: hex,
                    rgb: [r, g, b],
                    hsl: [h, s, l],
                    lab: lab
                });
            }

            // Calculate aggregated stats
            const totalL = samples.reduce((sum, s) => sum + s.hsl[2], 0);
            const totalS = samples.reduce((sum, s) => sum + s.hsl[1], 0);

            const stats = {
                avgLightness: totalL / samples.length,
                avgChroma: totalS / samples.length, // approximation of chroma via saturation
                sampleCount: samples.length
            };

            // Simple clustering/palette extraction (e.g. k-means or just median cut wrapper if we had time)
            // For now, let's take X representative colors by sorting by hue/lightness? 
            // Or just return the raw samples for the 3D view and let the UI pick 
            // a subset for the palette strip?
            // Let's pick 10 distinct colors for palette strip.

            // Very naive palette extraction: sort by Hue
            // Frequency-based quantization
            const colorCounts = {};
            const quantizeStep = 50; // Group similar colors (larger step = fewer, bigger groups)

            samples.forEach(s => {
                const r = Math.round(s.rgb[0] / quantizeStep) * quantizeStep;
                const g = Math.round(s.rgb[1] / quantizeStep) * quantizeStep;
                const b = Math.round(s.rgb[2] / quantizeStep) * quantizeStep;
                const key = `${r},${g},${b}`;

                if (!colorCounts[key]) {
                    colorCounts[key] = { count: 0, r, g, b, hex: s.color };
                }
                colorCounts[key].count++;
            });

            const sortedBuckets = Object.values(colorCounts).sort((a, b) => b.count - a.count);
            const topBuckets = sortedBuckets.slice(0, 10);

            // Normalize weights based on the sum of the top buckets (so the strip fills 100%)
            const totalTopCount = topBuckets.reduce((sum, b) => sum + b.count, 0);

            const palette = topBuckets.map(b => ({
                color: b.hex, // Use the representative hex from the bucket (or could re-average)
                weight: b.count / totalTopCount,
                count: b.count
            }));

            resolve({ samples, stats, palette });

        } catch (e) {
            reject(e);
        }
    });
};
